import { useNavigate } from "react-router-dom";

export type Cat = { stripe: string; chipBg: string; chipText: string };

export type CourseCardProps = {
  id: string;
  title: string;
  category: string;
  cat: Cat;
  progress: number;
  completed: boolean;
  coverUrl?: string | null;
  lessonLabel?: string | null;
};

export function CourseCard(p: CourseCardProps) {
  const navigate = useNavigate();
  const done = p.completed || p.progress >= 100;
  const fill = done ? "#3B6D11" : "#F44321";
  const track = done ? "#EAF3DE" : "#F0DDD2";
  const pct = Math.max(0, Math.min(100, Math.round(Number(p.progress) || 0)));
  const safeTitle = String(p.title ?? "");
  const initial = safeTitle.trim().charAt(0).toUpperCase() || "?";
  const safeCategory = String(p.category ?? "");
  return (
    <button
      onClick={() => navigate(`/app/course/${p.id}`)}
      className="relative w-full overflow-hidden rounded-[14px] border border-[#F0DDD2] bg-[#FFF6EC] text-left"
    >
      <span
        className="absolute left-1.5 top-3 bottom-3 w-[5px] rounded-full"
        style={{ background: p.cat.stripe }}
      />
      <div className="flex items-center gap-3 py-3 pl-5 pr-3">
        {p.coverUrl ? (
          <img
            src={p.coverUrl}
            alt=""
            className="h-12 w-12 flex-none rounded-[10px] object-cover"
          />
        ) : (
          <span
            className="flex h-12 w-12 flex-none items-center justify-center rounded-[10px] text-sm font-extrabold text-white"
            style={{ background: p.cat.stripe }}
          >
            {initial}
          </span>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <h3 className="truncate text-sm font-extrabold text-[#2A1810]">{safeTitle}</h3>
            <span className="flex-none text-[11px] font-bold" style={{ color: fill }}>
              {done ? "Done" : `${pct}%`}
            </span>
          </div>
          <div className="mt-1 flex items-center gap-1.5">
            <span
              className="rounded-full px-2 py-0.5 text-[10px] font-bold"
              style={{ background: p.cat.chipBg, color: p.cat.chipText }}
            >
              {safeCategory}
            </span>
            {p.lessonLabel && (
              <span className="text-[11px] font-semibold text-[#8A6050]">· {p.lessonLabel}</span>
            )}
          </div>
        </div>
      </div>
      <span className="absolute inset-x-0 bottom-0 h-1" style={{ background: track }}>
        <span
          className="block h-full"
          style={{ width: `${done ? 100 : pct}%`, background: fill }}
        />
      </span>
    </button>
  );
}

export const CATEGORY_PALETTE: Cat[] = [
  { stripe: "#F44321", chipBg: "#FCE2D2", chipText: "#B8431D" }, // coral
  { stripe: "#EF9F27", chipBg: "#FBEBCF", chipText: "#8A5A12" }, // amber
  { stripe: "#3B6D11", chipBg: "#E9F0DA", chipText: "#3B6D11" }, // green
  { stripe: "#A8552F", chipBg: "#F0E0D5", chipText: "#8A4A28" }, // rust
  { stripe: "#8A5A6A", chipBg: "#F0E0E5", chipText: "#7A4A5A" }, // mauve
];

export function pickCat(category: unknown): Cat {
  const s = String(category ?? "");
  let hash = 0;
  for (let i = 0; i < s.length; i++) {
    hash = (hash * 31 + s.charCodeAt(i)) | 0;
  }
  const idx = Math.abs(hash) % CATEGORY_PALETTE.length;
  return CATEGORY_PALETTE[idx];
}
